import { mockCreatedEntities, initialPlacements } from "@/components/ats-fbo/mock-data"
import { mockBlockingPreflightIssue, mockOnboardingDetails, mockPreflightIssues } from "./mock-data"
import type {
  OnboardingDecisions,
  OnboardingDetails,
  OnboardingOutcome,
  OnboardingValidationIssue,
  StepResult,
  SubmitResult,
} from "./onboarding-api.types"

function getMockResultParam(): string | null {
  if (typeof window === "undefined" || !window.location || typeof window.location.search !== "string") {
    return null
  }

  return new URLSearchParams(window.location.search).get("mockResult")
}

function upsertMockCreatedOrganization(id: string, name: string): void {
  const existingIndex = mockCreatedEntities.organizations.findIndex((org) => String(org.id) === id)
  const next = { id, name }
  if (existingIndex >= 0) {
    mockCreatedEntities.organizations[existingIndex] = next
    return
  }
  mockCreatedEntities.organizations.push(next)
}

export async function getMockOnboarding(): Promise<OnboardingDetails> {
  return Promise.resolve(mockOnboardingDetails)
}

/**
 * The preflight's answer in mock mode: the fixture when a new organization is created, nothing otherwise.
 * The records are named after the placement that is open and the organization being created, so the panel
 * shows the same names as the form above it.
 */
export async function preflightMockOnboarding(id: string, decisions: OnboardingDecisions): Promise<OnboardingValidationIssue[]> {
  if (decisions.workerOrganization?.kind !== "new") return []

  const workerName = initialPlacements.find((p) => p.atsId === id)?.atsSource.workerName ?? "Unknown worker"
  const organizationName = decisions.workerOrganization.organizationName ?? "New organization"
  const issues: OnboardingValidationIssue[] = mockPreflightIssues.map((issue) => ({
    ...issue,
    fixRecord: issue.field.startsWith("worker.") ? `worker 3746569 — ${workerName}` : `supplier 12049 — ${organizationName}`,
  }))
  if (getMockResultParam() === "blocking") issues.unshift(mockBlockingPreflightIssue)
  return Promise.resolve(issues)
}

export async function submitMockOnboarding(
  id: string,
  decisions: OnboardingDecisions,
): Promise<SubmitResult> {
  const mockResult = getMockResultParam()

  if (mockResult === "conflict") {
    return Promise.resolve({ success: false, isConflict: true })
  }

  if (mockResult === "error") {
    throw new Error("[Mock] Unexpected server error")
  }

  if (mockResult === "stepfail") {
    const outcome: OnboardingOutcome = {
      steps: [
        { step: "Assignment", succeeded: false, error: "[Mock] Assignment creation failed" },
      ],
      timestamp: new Date().toISOString(),
    }
    return Promise.resolve({ success: false, isConflict: false, outcome })
  }

  if (mockResult === "refused") {
    return Promise.resolve({ success: false, isConflict: false, issues: await preflightMockOnboarding(id, decisions) })
  }

  if (mockResult === "correction") {
    const steps: StepResult[] = []
    let workerProfileId: number | null = null
    let workerOrganizationId: number | null = null

    if (decisions.workerOrganization?.kind === "new") {
      steps.push({ step: "WorkerOrg", succeeded: true })
      workerOrganizationId = 900
      upsertMockCreatedOrganization("900", decisions.workerOrganization.organizationName ?? "Unknown")
    }
    if (decisions.worker?.kind === "add-profile" || decisions.worker?.kind === "create-contact") {
      steps.push({ step: "Worker", succeeded: true })
      workerProfileId = 800
      const placement = initialPlacements.find((p) => p.atsId === id)
      mockCreatedEntities.profiles.push({
        id: "800",
        name: placement?.atsSource.workerName ?? "Unknown",
        email: decisions.workerEmail ?? "",
        profileTypeCode: decisions.profileTypeCode ?? "",
        workerOrgId: workerOrganizationId ? String(workerOrganizationId) : null,
        contactId: "ct-800",
        contactStatus: "Active",
        profileStatus: "Active",
      })
    }

    if (steps.length > 0) {
      steps.push({ step: "Assignment", succeeded: false, error: "[Mock] Assignment creation failed for correction demo" })
      const outcome: OnboardingOutcome = {
        workerProfileId,
        workerOrganizationId,
        steps,
        timestamp: new Date().toISOString(),
      }
      return Promise.resolve({ success: false, isConflict: false, outcome })
    }
  }

  const outcome: OnboardingOutcome = {
    workOrderId: 9000 + Math.floor(Math.random() * 1000),
    steps: [],
    timestamp: new Date().toISOString(),
  }
  return Promise.resolve({ success: true, isConflict: false, outcome })
}
